import { AfterViewInit, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { SvcFilterEvent, SvcFilterValueEvent } from '../../classes/svc-filter-event';
import { SvcFilterField, SvcFilterFields } from '../../classes/svc-filter-field';
import { SvcFiltersComponent } from '../../svc-filters.component';

@Component({
  selector: 'svc-filters-sidebar',
  templateUrl: './svc-filters-sidebar.component.html',
  styleUrls: ['./svc-filters-sidebar.component.scss'],
})
export class SvcFiltersSidebarComponent implements OnChanges, AfterViewInit {
  @ViewChild(SvcFiltersComponent)
  public svcFilters: SvcFiltersComponent;

  @Input() public filters: SvcFilterField[] = [];
  @Input() public isLoading: boolean = false;
  @Input() public searchOnlyWhenHaveNewValues: boolean = false;
  @Input() public emitCloseAfterCleared: boolean = true;
  @Input() public emitCloseAfterCancelled: boolean = true;
  @Input() public emitCloseAfterSearchClicked: boolean = true;
  @Input() public useFirstMondayRule: boolean = false;
  @Input() public closeButtonTestId: string;
  @Input() public clearButtonTestId: string;
  @Input() public filtersTestId: string;
  @Input() public loadingTestId: string;
  @Input() public searchButtonTestId: string;

  @Output() public onCloseClicked = new EventEmitter<void>();
  @Output() public onFilterChanged = new EventEmitter<SvcFilterEvent>();
  @Output() public onFilterValueChanged = new EventEmitter<SvcFilterValueEvent>();
  @Output() public onFilterCleared = new EventEmitter<void>();
  @Output() public onFilterCancelled = new EventEmitter<SvcFilterEvent>();
  @Output() public onSearchClicked = new EventEmitter<SvcFilterEvent>();

  #lastFilters: { [key: string]: any } = {};

  protected hasFiltersPending = false;
  protected hasFiltersDefined = false;
  public isEmpty = true;
  public isTotallyEmpty = true;
  public isValid = true;

  public get fields(): SvcFilterFields[] {
    return this.svcFilters?.fields ?? [];
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if ('filters' in changes && this.svcFilters) {
      setTimeout(() => this.ngAfterViewInit());
    }
  }

  public ngAfterViewInit(): void {
    this.#lastFilters = this.svcFilters.fields.reduce((filters, field) => {
      filters[field.name] = field?.value ?? null;
      return filters;
    }, {});
    this.hasFiltersPending = false;
    this.hasFiltersDefined = !this.svcFilters.isTotallyEmpty;
    this.isEmpty = this.svcFilters.isEmpty;
    this.isTotallyEmpty = this.svcFilters.isTotallyEmpty;
    this.isValid = this.svcFilters.isValid;
  }

  protected close(): void {
    this.#internalCancelPendingFilters();
  }

  protected checkIfHasFiltersToDo(event: SvcFilterEvent): void {
    const newFilters = event.fields.reduce((filters, field) => {
      filters[field.name] = field?.value ?? null;
      return filters;
    }, {});
    this.hasFiltersPending = JSON.stringify(newFilters) != JSON.stringify(this.#lastFilters);
    this.onFilterChanged.emit(event);
  }

  protected search(fromClear: boolean = false): void {
    if (this.svcFilters?.isValid && !this.isLoading) {
      const newFilters = this.svcFilters.fields.reduce((filters, field) => {
        filters[field.name] = field?.value ?? null;
        return filters;
      }, {});
      this.hasFiltersPending = JSON.stringify(newFilters) != JSON.stringify(this.#lastFilters);
      if (!this.searchOnlyWhenHaveNewValues || this.hasFiltersPending) {
        this.#lastFilters = newFilters;
        this.hasFiltersPending = false;
        this.hasFiltersDefined = !this.svcFilters.isTotallyEmpty;
        if (!fromClear) {
          this.#emitSearchClicked();
        }
      }
    }
  }

  protected internalClearFilters(options?: { clearDisabled?: boolean; preventEmit?: boolean }): void {
    if (!this.isLoading && (this.hasFiltersDefined || this.hasFiltersPending)) {
      this.svcFilters.clearFiltersField(options ?? { preventEmit: true });
      if (this.hasFiltersDefined) {
        this.search(true);
        this.#emitFilterCleared();
      }
    }
  }

  public clearFilters(options?: { clearDisabled?: boolean; preventEmit?: boolean }): void {
    this.internalClearFilters(options);
  }

  public patchFiltersValue(fieldAndValues: { [key: string]: any }): void {
    fieldAndValues = fieldAndValues ?? {};
    this.svcFilters.patchValues(fieldAndValues);
    this.#lastFilters = this.#lastFilters
      ? Object.keys(this.#lastFilters).reduce((filters, fieldName) => {
        filters[fieldName] = fieldName in fieldAndValues ? fieldAndValues[fieldName] : this.#lastFilters[fieldName];
        return filters;
      }, {})
      : (fieldAndValues ?? {});
    this.hasFiltersPending = false;
    this.hasFiltersDefined = !this.svcFilters.isTotallyEmpty;
    this.isEmpty = this.svcFilters.isEmpty;
    this.isTotallyEmpty = this.svcFilters.isTotallyEmpty;
    this.isValid = this.svcFilters.isValid;
  }

  public cancelPendingFilters(): void {
    this.#internalCancelPendingFilters(false);
  }

  #internalCancelPendingFilters(fromInternal: boolean = true): void {
    if (this.hasFiltersPending) {
      this.#emitFilterCancelled(fromInternal);
    } else if (fromInternal) {
      this.onCloseClicked.emit();
    }
  }

  #emitSearchClicked(): void {
    this.isEmpty = this.svcFilters.isEmpty;
    this.isTotallyEmpty = this.svcFilters.isTotallyEmpty;
    this.isValid = this.svcFilters.isValid;
    this.onSearchClicked.emit({
      fields: this.svcFilters.fields,
    });
    if (this.emitCloseAfterSearchClicked) {
      this.onCloseClicked.emit();
    }
  }

  #emitFilterCleared(): void {
    this.hasFiltersDefined = false;
    this.hasFiltersPending = false;
    this.isEmpty = this.svcFilters.isEmpty;
    this.isTotallyEmpty = this.svcFilters.isTotallyEmpty;
    this.isValid = this.svcFilters.isValid;
    this.onFilterCleared.emit();
    if (this.emitCloseAfterCleared) {
      this.onCloseClicked.emit();
    }
  }

  #emitFilterCancelled(fromInternal: boolean): void {
    this.svcFilters.patchValues(this.#lastFilters);
    this.hasFiltersPending = false;
    this.hasFiltersDefined = !this.svcFilters.isTotallyEmpty;
    this.isEmpty = this.svcFilters.isEmpty;
    this.isTotallyEmpty = this.svcFilters.isTotallyEmpty;
    this.isValid = this.svcFilters.isValid;
    this.onFilterCancelled.emit({
      fields: this.svcFilters.fields,
    });
    if (fromInternal || this.emitCloseAfterCancelled) {
      this.onCloseClicked.emit();
    }
  }
}
